import { captureException } from '@sentry/react';
import { StateCreator } from 'zustand';

import { collaboratorService } from '@/services/database';

import { CollaboratorActions, CollaboratorState, getResourceKey } from '../types';

export const createCollaboratorActions: StateCreator<
    CollaboratorState & CollaboratorActions,
    [['zustand/immer', never]],
    [],
    CollaboratorActions
> = (set, get) => ({
    fetchCollaborators: async (resourceType, resourceId) => {
        const resourceKey = getResourceKey(resourceType, resourceId);
        const requestStatus = get().requestStatus[resourceKey];

        if (requestStatus === 'loading' || requestStatus === 'success') {
            return;
        }

        set({
            requestStatus: {
                ...get().requestStatus,
                [resourceKey]: 'loading',
            },
        });

        try {
            const response = await collaboratorService.list(resourceType, resourceId);
            const collaborators = Array.isArray(response) ? response : [];

            set({
                collaborators: {
                    ...get().collaborators,
                    [resourceKey]: collaborators,
                },
                requestStatus: {
                    ...get().requestStatus,
                    [resourceKey]: 'success',
                },
            });
        } catch (error) {
            captureException(error);

            set({
                requestStatus: {
                    ...get().requestStatus,
                    [resourceKey]: 'error',
                },
            });
        }
    },
    // Aliases
    addCollaborator: async (resourceType, resourceId, memberId, role) => {
        const key = getResourceKey(resourceType, resourceId);

        try {
            const collaborator = await collaboratorService.add(resourceType, resourceId, {
                member: memberId,
                permission: role,
                status: 'Active',
            });

            const existing = get().collaborators[key] ?? [];

            const alreadyExists = existing.some((c) => c.member?.id === collaborator.member?.id);

            if (alreadyExists) {
                return collaborator;
            }

            set({
                collaborators: {
                    ...get().collaborators,
                    [key]: [...existing, collaborator],
                },
            });

            return collaborator;
        } catch (error) {
            captureException(error);
            return null;
        }
    },
    updateCollaboratorPermission: async (resourceType, resourceId, collaboratorId, role) => {
        const key = getResourceKey(resourceType, resourceId);

        const previous = get().collaborators[key];

        try {
            // optimistic update
            set({
                collaborators: {
                    ...get().collaborators,
                    [key]: get().collaborators[key]?.map((c) =>
                        c.id === collaboratorId ? { ...c, permission: role } : c,
                    ),
                },
            });

            await collaboratorService.updatePermission(resourceType, resourceId, collaboratorId, role);
        } catch (error) {
            captureException(error);

            // rollback
            set({
                collaborators: {
                    ...get().collaborators,
                    [key]: previous,
                },
            });
        }
    },
    removeCollaborator: async (resourceType, resourceId, collaboratorId) => {
        const key = getResourceKey(resourceType, resourceId);

        const previous = get().collaborators[key];

        try {
            // optimistic removal
            set({
                collaborators: {
                    ...get().collaborators,
                    [key]: get().collaborators[key]?.filter((c) => c.id !== collaboratorId),
                },
            });

            await collaboratorService.delete(resourceType, resourceId, collaboratorId);
        } catch (error) {
            captureException(error);

            // rollback
            set({
                collaborators: {
                    ...get().collaborators,
                    [key]: previous,
                },
            });
        }
    },
});
